/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import SortableGrid from "../components/SortableGrid";
import api from "../lib/api";
import { fmtDate } from "../lib/format";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StatusBadge } from "./Dashboard";
import { Funnel, DownloadSimple, ListChecks, Trash, Table } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SpreadsheetModal } from "../components/SpreadsheetModal";

const STATUSES = [
  { value: "", label: "All statuses" },
  { value: "pending_review", label: "Pending Review" },
  { value: "confirmed", label: "Confirmed" },
  { value: "partially_shipped", label: "Partially Shipped" },
  { value: "fully_shipped", label: "Fully Shipped" },
];

export default function Orders() {
  const [pos, setPos] = useState([]);
  // Rows the grid is actually showing (after its search + date-range filter) so Export CSV
  // downloads exactly the filtered view, not every PO. Populated via onFilteredRowsChange.
  const [exportRows, setExportRows] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const sheetRows = exportRows.length ? exportRows : pos;
  const sheetColumns = [
    { header: "PO #", value: (p) => p.po_number || "", width: 130 },
    { header: "Customer", value: (p) => p.customer_name || "", width: 200 },
    { header: "Status", value: (p) => (p.status || "").replace("_", " ") },
    { header: "Order Date", value: (p) => p.order_date ? fmtDate(p.order_date) : "" },
    { header: "Req. Ship", value: (p) => reqShipDate(p) ? fmtDate(reqShipDate(p)) : "" },
    { header: "Lines", value: (p) => p.line_items?.length || 0, numeric: true },
    { header: "Ordered", value: (p) => p.total_ordered_qty, numeric: true },
    { header: "Shipped", value: (p) => p.total_shipped_qty, numeric: true },
    { header: "Open", value: (p) => p.total_open_qty, numeric: true },
    { header: "Value", value: (p) => p.total_amount, numeric: true },
    { header: "Currency", value: (p) => (p.currency || "USD").toUpperCase() },
    { header: "Age", value: (p) => p.aging_days, numeric: true },
    { header: "Source", value: (p) => (p.source_type || "").replace("_", " ") },
  ];
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const sourceEmail = params.get("source_email") || "";
  const nav = useNavigate();

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, sourceEmail]);

  const load = () => {
    const q = {};
    if (status) q.status = status;
    if (sourceEmail) q.source_email = sourceEmail;
    api.get("/po", { params: q }).then((r) => setPos(r.data));
  };

  const handleDelete = async (p, e) => {
    e.stopPropagation();
    const label = p.po_number ? `PO ${p.po_number}` : "this PO";
    if (!window.confirm(`Delete ${label} (${p.customer_name || "unknown customer"})? This cannot be undone.`)) return;
    try {
      await api.delete(`/po/${p.id}`);
      toast.success(`Deleted ${label}`);
      setPos((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete PO");
    }
  };

  const setStatus = (v) => {
    if (v) params.set("status", v); else params.delete("status");
    setParams(params, { replace: true });
  };

  const clearSourceFilter = () => {
    params.delete("source_email");
    setParams(params, { replace: true });
  };

  const columns = [
    {
      key: "po_number", header: "PO #",
      render: (p) => (
        <span className="text-mono" style={{ color: "var(--omh-primary)", fontWeight: 700 }}>
          {p.po_number || "—"}
          {p.is_silent_revision && (
            <span className="jde-badge alert" data-testid={`po-silent-rev-${p.id}`}
                  style={{ marginLeft: 6, fontSize: 9 }}
                  title="Silent revision — content changed with no revision keyword.">
              REV
            </span>
          )}
        </span>
      ),
    },
    { key: "customer_name", header: "Customer" },
    {
      key: "status", header: "Status",
      render: (p) => <StatusBadge status={p.status} />,
    },
    { key: "order_date", header: "Order Date", render: (p) => p.order_date ? fmtDate(p.order_date) : <span className="muted">—</span> },
    { key: "requested_ship_date", header: "Req. Ship", render: (p) => reqShipDate(p) ? fmtDate(reqShipDate(p)) : <span className="muted">—</span> },
    {
      key: "line_count", header: "Lines", align: "right",
      accessor: (p) => p.line_items?.length || 0,
    },
    { key: "total_ordered_qty", header: "Ord", align: "right", render: (p) => fmt(p.total_ordered_qty) },
    { key: "total_shipped_qty", header: "Shp", align: "right", render: (p) => fmt(p.total_shipped_qty) },
    {
      key: "total_open_qty", header: "Open", align: "right",
      render: (p) => <span style={{ fontWeight: 700 }}>{fmt(p.total_open_qty)}</span>,
    },
    {
      key: "total_amount", header: "Total", align: "right",
      render: (p) => p.total_amount ? `${p.currency || "USD"} ${fmt(p.total_amount)}` : <span className="muted">—</span>,
    },
    {
      key: "aging_days", header: "Age", align: "right",
      render: (p) => <span style={{ color: p.aging_days > 30 ? "var(--omh-alert)" : undefined, fontWeight: p.aging_days > 30 ? 700 : undefined }}>{p.aging_days}</span>,
    },
    {
      key: "source_type", header: "Source",
      render: (p) => <span className="muted" style={{ fontSize: 11 }}>{(p.source_type || "").replace("_", " ")}</span>,
    },
    {
      key: "_actions", header: "", sortable: false, align: "center",
      render: (p) => (
        <button
          className="jde-icon-btn"
          data-testid={`delete-po-${p.id}`}
          title="Delete PO"
          onClick={(e) => handleDelete(p, e)}
          style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--omh-alert)", padding: 4, display: "inline-flex" }}
        >
          <Trash size={15} weight="bold" />
        </button>
      ),
    },
  ];

  const exportCSV = (visible) => {
    const headers = columns.filter((c) => c.key !== "status" && c.key !== "_actions").map((c) => c.header);
    const lines = visible.map((p) => [
      p.po_number, p.customer_name, p.order_date ? fmtDate(p.order_date) : "", reqShipDate(p) ? fmtDate(reqShipDate(p)) : "",
      p.line_items?.length || 0, p.total_ordered_qty, p.total_shipped_qty, p.total_open_qty,
      p.total_amount || "", p.aging_days, p.source_type,
    ].map((x) => `"${(x ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `open_orders_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout breadcrumb={["Open Orders"]}>
      <div className="jde-toolbar">
        <ListChecks size={14} weight="bold" />
        <span style={{ fontWeight: 700, fontSize: 13 }}>OPEN ORDERS GRID</span>
        <div className="divider" />
        <Funnel size={12} />
        <select
          data-testid="orders-status-filter"
          className="jde-select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ width: 180 }}
        >
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        {sourceEmail && (
          <span className="jde-badge confirmed" style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="source-email-chip">
            Source: same email
            <button onClick={clearSourceFilter} style={{
              background: "transparent", border: "none", cursor: "pointer", color: "inherit",
              padding: 0, fontWeight: 700,
            }} data-testid="clear-source-filter">×</button>
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button className="jde-btn jde-btn-primary" onClick={() => setSheetOpen(true)} data-testid="open-spreadsheet"
          title="Open the currently filtered POs in a spreadsheet — drag-select cells to total, analyze, add formulas"
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Table size={12} weight="bold" /> Open in Spreadsheet
        </button>
        <button className="jde-btn" onClick={() => exportCSV(exportRows.length ? exportRows : pos)} data-testid="export-csv">
          <DownloadSimple size={12} weight="bold" /> Export CSV
        </button>
      </div>

      <div className="jde-panel" style={{ marginTop: 12 }}>
        <SortableGrid pageSize={100}
          columns={columns}
          rows={pos}
          onFilteredRowsChange={setExportRows}
          getRowKey={(p) => p.id}
          onRowClick={(p) => nav(`/po/${p.id}`)}
          defaultSort={{ key: "aging_days", dir: "desc" }}
          searchPlaceholder="Search PO#, customer, status, source…"
          testid="orders-grid"
          statusBar={(visible, all) => (
            <>
              <span>Showing {visible.length} of {all.length} POs</span>
              <span>{status ? `Filter: ${STATUSES.find((s) => s.value === status)?.label}` : "Filter: all"}</span>
            </>
          )}
        />
      </div>
      {sheetOpen && (
        <SpreadsheetModal
          title="Filtered POs"
          columns={sheetColumns}
          rows={sheetRows}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </Layout>
  );
}

function fmt(n) {
  if (n === null || n === undefined || n === "") return "—";
  return Number(n).toLocaleString();
}

// Header requested_ship_date is often empty because the delivery date lands on the
// line items (due_date). Fall back to the earliest line due date so it's never blank.
function reqShipDate(p) {
  if (p.requested_ship_date) return p.requested_ship_date;
  const dates = (p.line_items || [])
    .map((l) => l.due_date)
    .filter(Boolean)
    .sort();
  return dates[0] || null;
}
